import { useCallback, useMemo } from 'react';
import { ToolOptionsProps } from '../base-toolbar/toolbar-options/tool-options-renderer/ToolOptionsRenderer.types';
import { useStore } from 'src/contexts/StoreContext';
import { BaseToolbarProps, Tool } from '../base-toolbar/BaseToolbar.types';
import { EditLookupToolbarProps } from './EditLookupToolbar.types';
import { ACTIVE_TOOLS } from 'src/store/page-project/pageProject.constants';
import { EDIT_TOOLS } from '../base-toolbar/BaseToolbar.constants';
import { styles } from './EditLookupToolbar.styles';
import { useFrameToolLogic } from './edit-tools/FrameTool.logic';
import { useOrientationToolLogic } from './edit-tools/OrientationTool.logic';
import { useFiltersToolLogic } from './edit-tools/FiltersTool.logic';
import { useAdjustmentsToolLogic } from './edit-tools/AdjustmentsTool.logic';
import { useReplaceToolLogic } from './edit-tools/ReplaceTool.logic';
import { useRestoreToolLogic } from './edit-tools/RestoreTool.logic';
import { useUndoToolLogic } from './edit-tools/UndoTool.logic';
import { useRedoToolLogic } from './edit-tools/RedoTool.logic';
import { useRoute } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

export const useEditLookupToolbarLogic = (props: EditLookupToolbarProps) => {
  const { onOpenTool, onCloseTool: _onCloseToolHandler, activeProduct } = props;
  const {
    project: { setActiveTool, activeTool },
  } = useStore();
  const { isDesktop } = useResponsiveScreen();

  const onCloseTool = useCallback(
    async (tool: Tool<ToolOptionsProps>) => {
      // in order to re-activate frame selection after closing any other tool
      setActiveTool(ACTIVE_TOOLS.frame);
      if (_onCloseToolHandler) {
        await _onCloseToolHandler(tool);
      }
    },
    [_onCloseToolHandler, setActiveTool],
  );

  const editToolProps = { onOpenTool, onCloseTool };
  const route = useRoute();
  const isOldEditor = ![ROUTE_NAMES.EditorNew, ROUTE_NAMES.Edit].includes(route?.name as ROUTE_NAMES);
  const isOrientationEnabled = activeProduct?.isOrientationEnabled;
  // const FrameTool = useFrameToolLogic(editToolProps);
  const orientationToolData = useOrientationToolLogic(editToolProps);
  const OrientationTool = isOrientationEnabled ? orientationToolData : null;
  const FiltersTool = useFiltersToolLogic(editToolProps);
  const AdjustmentsTool = useAdjustmentsToolLogic(editToolProps);
  const replaceToolData = useReplaceToolLogic(editToolProps);
  const ReplaceTool = isOldEditor ? replaceToolData : null; //only visible for old editor
  const RestoreTool = useRestoreToolLogic(editToolProps);
  const undoToolData = useUndoToolLogic(editToolProps);
  const UndoTool = isOldEditor || !isDesktop ? undoToolData : null; //only visible for old editor or mobile
  const redoToolData = useRedoToolLogic(editToolProps);
  const RedoTool = isOldEditor || !isDesktop ? redoToolData : null; //only visible for old editor or mobile

  const tools: Tool<ToolOptionsProps>[] = useMemo(() => {
    return [
      // FrameTool,
      OrientationTool,
      FiltersTool,
      AdjustmentsTool,
      ReplaceTool,
      RestoreTool,
      UndoTool,
      RedoTool,
    ].filter(tool => !!tool);
  }, [OrientationTool, FiltersTool, AdjustmentsTool, ReplaceTool, RestoreTool, UndoTool, RedoTool]);

  return {
    tools,
    initialSelectedToolCode: EDIT_TOOLS.frame,
    style: styles.lookupToolbar,
    maxHeight: 600,
    maxWidth: 270,
    selectedToolCode: activeTool,
    setSelectedToolCode(tool) {
      setActiveTool(tool as ACTIVE_TOOLS);
    },
    isEdit: true,
  } satisfies BaseToolbarProps;
};
